<template>
  <base-edge :path="detourPath.path" :style="style" :source-x="props.sourceX" :source-y="props.sourceY" :target-x="props.targetX" :target-y="props.targetY" :source-position="props.sourcePosition" :target-position="props.targetPosition" />
</template>

<script setup>
import useFlowStage from '@/hooks/flow-stage';
import { BaseEdge, getBezierPath } from '@vue-flow/core';
import { computed } from 'vue';
const { uniqueNodesFun, generatePath } = useFlowStage();
// props were passed from the slot using `v-bind="customEdgeProps"`
// const props = defineProps(['sourceX', 'sourceY', 'targetX', 'targetY', 'sourcePosition', 'targetPosition']);
const props = defineProps({
  id: {
    type: String,
    required: true
  },
  sourceX: {
    type: Number,
    required: true
  },
  sourceY: {
    type: Number,
    required: true
  },
  targetX: {
    type: Number,
    required: true
  },
  targetY: {
    type: Number,
    required: true
  },
  sourcePosition: {
    type: String,
    required: true
  },
  targetPosition: {
    type: String,
    required: true
  },
  data: {
    type: Object,
    required: false
  },
  markerEnd: {
    type: String,
    required: false
  },
  style: {
    type: Object,
    required: false
  },
  nodes: {
    type: [Array, Object],
    required: () => []
  },
  edges: {
    type: [Array, Object],
    required: () => []
  }
});

const lineId = computed(() => props.id);
const nodes = computed(() => props.nodes);
const edges = computed(() => props.edges);
const path = computed(() => getBezierPath(props));
console.log(path);
// 节点1/2高
const h14 = 14;
// Y轴偏移量
const offsetY = 15;

// 重新计算path
//  source
const lineObj = edges.value?.find((item) => {
  return item.id == lineId.value;
});
const sourceNode = nodes.value?.find((item) => {
  return item.id == lineObj?.source;
});
const targetNode = nodes.value?.find((item) => {
  return item.id == lineObj?.target;
});
// 传入x1 x2  获取 x1 x2 之间的节点 并排序
const filterAndSortNodesByPositionX = (data, x1, x2) => {
  return data.filter((node) => node.position.x > x1 && node.position.x < x2).sort((a, b) => a.position.x - b.position.x);
};

const detourPath = computed(() => {
  // 1 判断两个节点之间是否有  中间节点     Y轴一定  X轴不等  中间是否有 大于 小于  如果有  有几个  且 按照  X大小排序
  // 判断 Y 轴是否一致
  if (sourceNode?.position?.y == targetNode?.position?.y) {
    // 获取所有Y轴一致的
    const yArray = nodes.value?.filter((e) => {
      return e.position.y === sourceNode?.position?.y;
    });
    if (yArray && yArray?.length) {
      // 传入x1 x2  获取 x1 x2 之间的节点 并排序
      const yBetweenArray = filterAndSortNodesByPositionX(yArray, sourceNode?.position?.x, targetNode?.position?.x);
      if (yBetweenArray && yBetweenArray?.length) {
        if (yBetweenArray[0].level - sourceNode.level > 1 && targetNode.level - yBetweenArray[yBetweenArray.length - 1].level > 1) {
          // 6个点
          const point1 = { x: sourceNode?.position?.x + sourceNode?.data?.nodeWidth, y: sourceNode?.position?.y + h14 };
          const point2 = { x: yBetweenArray[0]?.position.x - yBetweenArray[0]?.data?.nodeWidth, y: yBetweenArray[0]?.position.y + h14 };
          const point3 = { x: yBetweenArray[0]?.position.x, y: yBetweenArray[0]?.position.y - offsetY };
          const point4 = { x: yBetweenArray[yBetweenArray.length - 1]?.position.x + yBetweenArray[yBetweenArray.length - 1]?.data?.nodeWidth, y: yBetweenArray[yBetweenArray.length - 1]?.position.y - offsetY };
          const point5 = { x: yBetweenArray[yBetweenArray.length - 1]?.position.x + yBetweenArray[yBetweenArray.length - 1]?.data?.nodeWidth + 63, y: yBetweenArray[yBetweenArray.length - 1]?.position?.y + h14 };
          const point6 = { x: targetNode?.position?.x, y: targetNode?.position?.y + h14 };
          const path = generatePath(point1, point2, point3, point4, point5, point6);
          return { path: `${path}`, path1: (point3.x + point4.x) / 2, path2: (point3.y + point4.y) / 2 };
        } else if (targetNode.level - yBetweenArray[yBetweenArray.length - 1].level > 1) {
          // 5个点
          const point1 = { x: sourceNode?.position?.x + sourceNode?.data?.nodeWidth, y: sourceNode?.position?.y + h14 };
          const point2 = { x: yBetweenArray[0]?.position.x, y: yBetweenArray[0]?.position.y - offsetY };
          const point3 = { x: yBetweenArray[yBetweenArray.length - 1]?.position.x + yBetweenArray[yBetweenArray.length - 1]?.data?.nodeWidth, y: yBetweenArray[0]?.position.y - offsetY };
          const point4 = { x: yBetweenArray[yBetweenArray.length - 1]?.position.x + yBetweenArray[yBetweenArray.length - 1]?.data?.nodeWidth + 63, y: yBetweenArray[yBetweenArray.length - 1]?.position.y + h14 };
          const point5 = { x: targetNode?.position?.x, y: targetNode?.position?.y + h14 };
          const path = generatePath(point1, point2, point3, point4, point5);
          return { path: `${path}`, path1: (point2.x + point3.x) / 2, path2: (point2.y + point3.y) / 2 };
        } else if (yBetweenArray[0].level - sourceNode.level > 1) {
          // 5个点
          const point1 = { x: sourceNode?.position?.x + sourceNode?.data?.nodeWidth, y: sourceNode?.position?.y + h14 };
          const point2 = { x: yBetweenArray[0]?.position.x - yBetweenArray[0]?.data?.nodeWidth, y: yBetweenArray[0]?.position.y + h14 };
          const point3 = { x: yBetweenArray[0]?.position.x, y: yBetweenArray[0]?.position.y - offsetY };
          const point4 = yBetweenArray?.length > 1 ? { x: yBetweenArray[yBetweenArray.length - 1]?.position.x + yBetweenArray[yBetweenArray.length - 1]?.data?.nodeWidth, y: yBetweenArray[yBetweenArray.length - 1]?.position.y - offsetY } : { x: yBetweenArray[0]?.position.x + yBetweenArray[0]?.data?.nodeWidth, y: yBetweenArray[0]?.position.y - offsetY };
          const point5 = { x: targetNode?.position?.x, y: targetNode?.position?.y + h14 };
          const path = generatePath(point1, point2, point3, point4, point5);
          return { path: `${path}`, path1: (point2.x + point3.x) / 2, path2: (point2.y + point3.y) / 2 };
        } else {
          // 4个点
          const point1 = { x: sourceNode?.position?.x + sourceNode?.data?.nodeWidth, y: sourceNode?.position?.y + h14 };
          // 情况 1   连续点 与 间隔点
          const point2 = { x: yBetweenArray[0]?.position.x, y: yBetweenArray[0]?.position.y - offsetY };
          const point3 = yBetweenArray?.length > 1 ? { x: yBetweenArray[yBetweenArray.length - 1]?.position.x + yBetweenArray[yBetweenArray.length - 1]?.data?.nodeWidth, y: yBetweenArray[yBetweenArray.length - 1]?.position.y - offsetY } : { x: yBetweenArray[0]?.position.x + yBetweenArray[0]?.data?.nodeWidth, y: yBetweenArray[0]?.position.y - offsetY };
          const point4 = { x: targetNode?.position?.x, y: targetNode?.position?.y + h14 };
          const path = generatePath(point1, point2, point3, point4);
          return { path: `${path}`, path1: (point2.x + point3.x) / 2, path2: (point2.y + point3.y) / 2 };
        }
      }
    }
  }
  if (sourceNode?.position?.y != targetNode?.position?.y) {
    // 获取两节点之间的X轴
    const xArray = nodes.value?.filter((e) => {
      return sourceNode.position.x < e.position.x && e.position.x < targetNode.position.x;
    });
    if (xArray && xArray.length) {
      const xArrayCopy = JSON.parse(JSON.stringify(xArray));
      // 去除重复的X轴
      const uniqueNodes = uniqueNodesFun(xArrayCopy);
      // 是否有相同的Y轴  相等则证明同一水平线 需要绕路
      const equalY = [];
      xArray?.forEach((e) => {
        if (e.position.y == sourceNode.position.y) {
          equalY.push(e);
        }
      });
      if (equalY && equalY?.length) {
        equalY.sort((a, b) => a.position.x - b.position.x);
        console.log('equalY', equalY);
        // 绕路逻辑 是否有间隔点
        if (equalY[0].level - sourceNode.level > 1) {
          // 5个点 绕路逻辑 + 补路逻辑
          const point1 = { x: sourceNode?.position?.x + sourceNode?.data?.nodeWidth, y: sourceNode?.position?.y + h14 };
          const point2 = { x: equalY[0].position?.x - equalY[0]?.data?.nodeWidth, y: sourceNode?.position?.y + h14 };
          const point3 = { x: equalY[0].position?.x, y: sourceNode?.position?.y - offsetY };
          const point4 = { x: uniqueNodes[uniqueNodes.length - 1].position?.x + uniqueNodes[uniqueNodes.length - 1]?.data?.nodeWidth, y: sourceNode.position?.y - offsetY };
          const point5 = { x: targetNode.position.x, y: targetNode.position.y + h14 };
          const path = generatePath(point1, point2, point3, point4, point5);
          return { path: `${path}`, path1: (point3.x + point4.x) / 2, path2: (point3.y + point4.y) / 2 };
        }
        // 4个点 绕路逻辑 + 补路逻辑
        const point1 = { x: sourceNode?.position?.x + sourceNode?.data?.nodeWidth, y: sourceNode?.position?.y + h14 };
        const point2 = { x: equalY[0].position?.x, y: equalY[0].position?.y - offsetY };
        const point3 = { x: uniqueNodes[uniqueNodes.length - 1].position?.x + uniqueNodes[uniqueNodes.length - 1]?.data?.nodeWidth, y: sourceNode.position?.y - offsetY };
        const point4 = { x: targetNode.position.x, y: targetNode.position.y + h14 };
        const path = generatePath(point1, point2, point3, point4);
        return { path: `${path}`, path1: (point3.x + point4.x) / 2, path2: (point3.y + point4.y) / 2 };
      } else {
        // 补路逻辑
        // 去除重复的X轴
        const uniqueNodes = uniqueNodesFun(xArray);
        const point1 = { x: sourceNode?.position?.x + sourceNode?.data?.nodeWidth, y: sourceNode?.position?.y + h14 };
        const point2 = { x: uniqueNodes[uniqueNodes?.length - 1].position.x + uniqueNodes[uniqueNodes?.length - 1]?.data?.nodeWidth, y: sourceNode?.position?.y + h14 };
        const point3 = { x: targetNode.position.x, y: targetNode.position.y + h14 };
        const path = generatePath(point1, point2, point3);
        return { path: `${path}`, path1: (point1.x + point2.x) / 2, path2: (point1.y + point2.y) / 2 };
      }
    }
  }
  const x1 = (sourceNode?.data?.nodeWidth || 0) + sourceNode?.position?.x + 2.5;
  const y1 = sourceNode?.position?.y + h14;

  const x2 = targetNode?.position?.x - 1.5 > 0 ? targetNode?.position?.x - 1.5 : 0;
  const y2 = targetNode?.position?.y + h14;

  const position1 = `M${x1},${y1}`;
  const position2 = `${x2},${y2}`;
  const Mave = (x1 + x2) / 2;
  const C1 = `C${Mave},${y1} ${Mave},${y2}`;
  return { path: `${position1} ${C1} ${position2}`, path1: (x1 + x2) / 2, path2: (y1 + y2) / 2 };
});
</script>
<style scoped lang="less">
@import './custom-edge.less';
</style>
